import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import { MatLegacyDialogRef as MatDialogRef } from "@angular/material/legacy-dialog";
import { RouterState } from "@angular/router";
import { Store } from "@ngrx/store";
import { CustomerSupportService } from "@obg/core.customer-support";
import { batchActions } from "@obg/ngrx-utils";
import { CommonState, MarketingSettingsSelector } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AUTH_MESSAGE_CODES,
	AUTH_MESSAGE_CONTEXT,
	AuthSelector,
	CORE_ROUTE,
	CoreState,
	CustomerManagementAction,
	CustomerManagementSelector,
	JurisdictionPrefixPipe,
	LoginMethod,
	MessageSelector,
	MessageType,
	SessionAction,
} from "@obg/ngx.core";
import { DeviceConfigService, ignoreEmpty, ignoreNil } from "@obg/ngx.global";
import { RouteAction, RouteSelector } from "@obg/ngx.router";
import { combineLatest, merge, of, Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

import { OpenIdConnectSelector } from "../sdk/open-id-connect.selector";

import { OpenIdConnectCacheService } from "./open-id-connect-cache.service";
import { OPEN_ID_CONNECT_CURRENT_JOURNEY_KEY, OpenIdConnectProviders } from "./open-id-connect.const";

@Component({
	selector: "obg-m-open-id-connect-container",
	styleUrls: ["./open-id-connect.container.scss"],
	templateUrl: "./open-id-connect.container.html",
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class OpenIdConnectContainer implements OnInit, OnDestroy {
	oidcProvider: OpenIdConnectProviders | undefined;

	@HostBinding("class.obg-m-open-id-connect-container") hostCssClass = true;
	private data$$: Subscription | undefined;
	private isDesktop = this.deviceConfigService.isDesktop();

	constructor(
		private store: Store<CommonState & CoreState & RouterState>,
		private changeDetector: ChangeDetectorRef,
		private openIdConnectCacheService: OpenIdConnectCacheService,
		private dialog: MatDialogRef<OpenIdConnectContainer>,
		private messageSelector: MessageSelector,
		private sessionAction: SessionAction,
		private selector: OpenIdConnectSelector,
		private marketingSettingsSelector: MarketingSettingsSelector,
		private deviceConfigService: DeviceConfigService,
		private alertService: AbstractTranslatableAlertService,
		private jurisdictionPrefixPipe: JurisdictionPrefixPipe,
		private customerManagementAction: CustomerManagementAction,
		private customerSupportService: CustomerSupportService
	) {}

	ngOnInit() {
		this.oidcProvider = this.getOidcProvider();
		const loginWithToken$ = combineLatest([
			this.store.select(this.selector.getToken()).pipe(ignoreNil()),
			this.store.select(RouteSelector.isNavigating),
		]).pipe(
			filter(([, isNavigating]) => !isNavigating),
			take(1),
			tap(([token]) => {
				const authenticationMethod = this.getAuthenticationMethod();
				this.store.dispatch(
					this.sessionAction.loginWithToken({
						token,
						authenticationMethod,
					})
				);
			})
		);

		const invalidCredentials$ = this.store.pipe(
			this.messageSelector.getMessageCodes$({
				messageCode: AUTH_MESSAGE_CODES.login.invalidCredentials,
				context: AUTH_MESSAGE_CONTEXT.loginWithToken,
				messageType: MessageType.Error,
			}),
			filter((message) => !!message.length),
			tap(() => {
				if (this.isDesktop) {
					this.dialog.close();
				}

				switch (this.oidcProvider) {
					case OpenIdConnectProviders.MitId:
						const queryParamsMitid = { displayWelcomeMessage: true };
						this.store.dispatch(
							RouteAction.navigate(CORE_ROUTE.registration.register, undefined, {
								queryParams: queryParamsMitid,
							})
						);
						break;
					case OpenIdConnectProviders.Google:
						const queryParamsGoogle = { connectedAccountMissing: true };
						this.store.dispatch(
							RouteAction.navigate(CORE_ROUTE.auth.login, undefined, {
								queryParams: queryParamsGoogle,
								replaceUrl: true,
							})
						);
						break;
				}

				this.openIdConnectCacheService.removeShouldRememberUserData();
				this.openIdConnectCacheService.removeCurrentJourney();
			})
		);

		const customerNotAllowed$ = this.store.pipe(
			this.messageSelector.getMessageCodes$({
				messageCode: AUTH_MESSAGE_CODES.login.customerNotAllowed,
				context: AUTH_MESSAGE_CONTEXT.loginWithToken,
				messageType: MessageType.Error,
			}),
			ignoreEmpty(),
			tap((messages) => {
				if (this.isDesktop) {
					this.dialog.close();
				}
				this.alertService.open(
					{
						id: "LOGIN_ERROR_ALERT",
						title: this.jurisdictionPrefixPipe.transform("login.title.login-error"),
						message: this.jurisdictionPrefixPipe.transform(`login.${messages[0].toLowerCase()}`),
						buttons: [
							{
								text: "general.close",
								handler: () => this.store.dispatch(RouteAction.navigate(CORE_ROUTE.auth.login)),
							},
							{
								text: "general.contact",
								handler: () => this.customerSupportService.openSupportPageInNewTab(),
							},
						],
					},
					[]
				);

				this.openIdConnectCacheService.removeShouldRememberUserData();
			})
		);

		const onTermsAndConditionsDismiss$ = this.store
			.select(CustomerManagementSelector.hasDismissedLatestTermsAndConditions)
			.pipe(
				filter((hasDismissed) => hasDismissed === true),
				take(1),
				tap(() => {
					this.store.dispatch(RouteAction.setQueryParams({}, true));
					this.store.dispatch(this.customerManagementAction.clearDismissTermsAndConditions());
					if (this.isDesktop) {
						this.dialog.close();
						return;
					}
					this.store.dispatch(RouteAction.navigate(CORE_ROUTE.home));
				})
			);

		const consents$ = combineLatest([
			this.store.select(this.selector.getToken()),
			this.store.select(this.marketingSettingsSelector.hasGivenConsents()),
		]).pipe(
			filter(([token, consents]) => !!token && !!consents),
			take(1),
			tap(([token]: [string, boolean]) => {
				const queryParams = { displayWelcomeMessage: true };
				const authenticationMethod = this.getAuthenticationMethod();

				this.store.dispatch(
					batchActions(
						RouteAction.navigate(CORE_ROUTE.home, undefined, { queryParams }),
						this.sessionAction.loginWithToken({
							token,
							authenticationMethod,
						})
					)
				);
			})
		);

		const setLastActiveUser$ = combineLatest([
			this.store.select(AuthSelector.isAuthenticated),
			this.openIdConnectCacheService.getShouldRememberUserData$(),
		]).pipe(
			filter(([isAuth, shouldRememberUserData]) => !!isAuth && !!shouldRememberUserData),
			tap(([, shouldRememberUserData]) => {
				const now = new Date();
				const nowInMinutes = now.setMinutes(now.getMinutes());
				const expirationTime = shouldRememberUserData?.expiry;
				const shouldSetActiveUser = shouldRememberUserData?.shouldRememberUser;

				if (expirationTime && nowInMinutes > expirationTime) {
					this.openIdConnectCacheService.removeShouldRememberUserData();
					return;
				}

				if (!shouldSetActiveUser) {
					this.openIdConnectCacheService.removeLastActiveUser();
				}

				if (shouldSetActiveUser && shouldRememberUserData?.cpr) {
					this.openIdConnectCacheService.setLastActiveUser(shouldRememberUserData?.cpr);
				}

				this.openIdConnectCacheService.removeShouldRememberUserData();
			})
		);

		const closeDialog$ = combineLatest([
			this.store.pipe(RouteSelector.getName$),
			this.store.select(RouteSelector.isNavigating),
			this.store.select(AuthSelector.isAuthenticated),
			of(this.deviceConfigService.isDesktop()),
		]).pipe(
			filter(
				([routeName, isNavigating, isAuth, isDesktop]) =>
					!isNavigating && (routeName === CORE_ROUTE.registration.register || isAuth) && isDesktop
			),
			switchMap(() => navigateToReturnUrl$),
			tap(() => this.dialog.close())
		);

		const navigateToReturnUrl$ = combineLatest([
			this.store.select(RouteSelector.isNavigating),
			this.store.select(AuthSelector.isAuthenticated),
			this.openIdConnectCacheService.getReturnUrl$(),
			of(this.deviceConfigService.isDesktop()),
		]).pipe(
			filter(([isNavigating, isAuth, returnUrl]) => !isNavigating && isAuth && !!returnUrl),
			tap(([, , returnUrl, isDesktop]) => {
				const queryParams = isDesktop ? { displayLoginMessage: true } : undefined;
				this.store.dispatch(RouteAction.navigateByUrl(returnUrl || CORE_ROUTE.home, { queryParams }));
			})
		);

		this.data$$ = merge(
			loginWithToken$,
			invalidCredentials$,
			closeDialog$,
			setLastActiveUser$,
			customerNotAllowed$,
			consents$,
			onTermsAndConditionsDismiss$
		).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		if (this.data$$) {
			this.data$$.unsubscribe();
		}
	}

	private getAuthenticationMethod(): LoginMethod {
		switch (this.oidcProvider) {
			case OpenIdConnectProviders.MitId:
				return LoginMethod.MitId;
			case OpenIdConnectProviders.Google:
				return LoginMethod.Google;
			default:
				return LoginMethod.MitId;
		}
	}

	private getOidcProvider(): OpenIdConnectProviders | undefined {
		return (sessionStorage.getItem(OPEN_ID_CONNECT_CURRENT_JOURNEY_KEY) as OpenIdConnectProviders) || undefined;
	}
}
